import React from 'react';
import { CellContext } from '@tanstack/react-table';
import { formatMetricValue, MetricProps } from '../../utils';
import LongContentPopover from '@hud/web-components/LongContentPopover/LongContentPopover';
import { TimeFrame } from '@hud/portal-dtos/webapp-common.dto';
import {
  InvocationSourcesFunctionData,
  FunctionData,
} from '@hud/portal-dtos/webapp-function.dto';
import { Endpoint } from '@hud/portal-dtos/webapp-endpoint.dto';
import { MessageQueue } from '@hud/portal-dtos/webapp-message-queue.dto';
import { isTimeFrameLessThanDay } from '@hud/web-components/utils';
import { FormattedRuns } from './runsCellRenderer.styles';

type EntityWithRuns =
  | FunctionData
  | InvocationSourcesFunctionData
  | Endpoint
  | MessageQueue;

export function formatWithNormalizedValue({
  totalRuns,
  normalizedRuns,
  format,
}: {
  totalRuns: number;
  normalizedRuns: number;
  format: MetricProps['format'];
}) {
  const formattedRuns = formatMetricValue({
    value: totalRuns,
    format: format,
  });
  const formattedNormalizedRuns = formatMetricValue({
    value: normalizedRuns,
    format: 'abbreviated',
  });
  return (
    <FormattedRuns>
      <span className="totalRuns">{formattedRuns}</span>
      <span className="normalizedRuns">({formattedNormalizedRuns})</span>
    </FormattedRuns>
  );
}

function isMainEntity(
  obj: EntityWithRuns,
): obj is FunctionData | Endpoint | MessageQueue {
  return !!obj && typeof obj === 'object' && 'firstSeenAt' in obj;
}

// not supposed to happen in happy flow, but theoretically possible if the original function's firstSeenAt is not available
function isInvocationSourceData(obj: EntityWithRuns): obj is InvocationSourcesFunctionData {
  return !!obj && typeof obj === 'object' && 'firstInvokedAt' in obj;
}

export function formatRunsMetric<TData extends EntityWithRuns>(
  format: MetricProps['format'],
  timeFrame: TimeFrame,
  firstSeenAt?: Date,
) {
  return function cellFormatter(cell: CellContext<TData, number>) {
    const original = cell.row.original;
    const totalRuns: number = cell.getValue();
    const normalizedRuns = original.metrics.normalizedRuns.value;

    const firstSeen =
      firstSeenAt ??
      (isMainEntity(original)
        ? original.firstSeenAt
        : isInvocationSourceData(original)
          ? original.firstInvokedAt
          : undefined);

    if (isTimeFrameLessThanDay(timeFrame, firstSeen) || !normalizedRuns) {
      // If the time frame is less than a day or normalized runs are not available,
      return formatMetricValue({ value: totalRuns, format: format });
    }
    return formatWithNormalizedValue({ totalRuns, normalizedRuns, format });
  };
}

function formatTooltipContent<TData extends EntityWithRuns>(
  cell: CellContext<TData, number>,
  timeFrame: TimeFrame,
  firstSeenAt?: Date,
) {
  const normalizedRuns = cell.row.original.metrics.normalizedRuns.value;
  if (isTimeFrameLessThanDay(timeFrame, firstSeenAt) || !normalizedRuns) {
    return;
  }
  const totalRuns = cell.row.original.metrics.runs.value;
  const formattedTotal = formatMetricValue({ value: totalRuns, format: 'number' });
  const formattedNormalized = formatMetricValue({
    value: normalizedRuns,
    format: 'number',
  });
  return `Total Runs: ${formattedTotal}, Runs per Day: ${formattedNormalized}`;
}

export default function renderRunsMetric<
  TData extends EntityWithRuns,
  TValue extends number,
>(timeFrame: TimeFrame, firstSeenAt?: Date) {
  const formatter = formatRunsMetric<TData>('number', timeFrame, firstSeenAt);
  return function cellRenderer(cell: CellContext<TData, TValue>) {
    const tooltipContent = formatTooltipContent(cell, timeFrame, firstSeenAt);
    return (
      <LongContentPopover
        alwaysShowPopoverOnHover={!!tooltipContent}
        popoverContent={tooltipContent}
      >
        {formatter(cell)}
      </LongContentPopover>
    );
  };
}
